import { useCallback, useEffect, useRef, useState } from "react";

import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { PlusIcon } from "@lucide/icons";
import uuid from "uuid";

import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Card, CardGroup } from "simplydo/react/ui/card";
import { Container } from "simplydo/react/ui/container";
import { Divider } from "simplydo/react/ui/divider";
import { Grid, GridColumn, GridRow } from "simplydo/react/ui/grid";
import { H1, H2 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import {
  Placeholder,
  PlaceholderLine,
  PlaceholderParagraph,
} from "simplydo/react/ui/placeholder";

import api from "api";
import DiscoverImage from "src/images/discover.png";
import { useAppSelector } from "store";
import util from "utils/utils";

import NewChallengeModal from "components/challenges/NewChallengeModal";
import ChallengeCard from "components/lib/Cards/ChallengeCard";
import { OrgSwitcherDropdown } from "components/lib/OrgSwitcherTray";
import { EmptyBox } from "components/lib/UI";

const Challenges = () => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);

  const [isLoading, setIsLoading] = useState(true);
  const currentFetchId = useRef("");
  const [newChallengeOpen, setNewChallengeOpen] = useState(false);
  const [challengeData, setChallengeData] = useState<
    OpenAPI.GET<"/challenges/all">["response"] | null
  >(null);
  const [searchQuery, setSearchQuery] = useState("");
  const theme = useTheme();

  const { challenges } = challengeData ?? {};

  const fetchChallenges = useThrottle(
    (query) => {
      const fetchId = uuid.v4();
      currentFetchId.current = fetchId;

      api.challenges.getAll(
        { query },
        (data) => {
          if (fetchId !== currentFetchId.current) return;
          setChallengeData(data);
          setIsLoading(false);
        },
        (err) => {
          toast.error(err.message);
          setIsLoading(false);
        },
      );
    },
    400,
    [],
  );

  const getChallenges = useCallback(
    (query: string) => {
      setIsLoading(true);
      setSearchQuery(query);
      fetchChallenges(query);
    },
    [fetchChallenges],
  );

  useEffect(() => {
    getChallenges("");
  }, [getChallenges, user?.ownerOrganisation?._id]);

  return (
    <div>
      {newChallengeOpen ? (
        <NewChallengeModal
          newChallengeOpen={newChallengeOpen}
          onCloseModal={() => setNewChallengeOpen(false)}
        />
      ) : null}
      <Helmet title={t("common:capitalise", { key: "generic.challenges" })} />
      <div style={{ marginTop: 20 }}>
        <Container>
          <Grid stackable columns={16}>
            <GridRow columns={16}>
              <GridColumn span={16} computer={11}>
                <H1 style={{ color: "#3b3b3b", margin: "0px" }}>
                  {user
                    ? t("common:capitalise", { key: "generic.challenges" })
                    : t("challenges.yours.sidebar.all")}
                </H1>
                {user && (user.ownerOrganisations?.length ?? 0) > 1 ? (
                  <div style={{ fontSize: "0.9rem" }}>
                    in{" "}
                    <span style={{ fontWeight: 600 }}>
                      {user.ownerOrganisation?.name}
                    </span>
                    {" - "}
                    <OrgSwitcherDropdown
                      basic
                      trigger={
                        <a
                          tabIndex={0}
                          role="button"
                          onClick={(e) => e.preventDefault()}
                        >
                          Change organisation
                        </a>
                      }
                    />
                  </div>
                ) : null}
              </GridColumn>
              <GridColumn
                span={16}
                computer={5}
                textAlign="right"
                verticalAlign="bottom"
              >
                {util.canCreateChallenges(user) && (
                  <Button
                    size={!theme.sizes.isComputer ? "small" : "medium"}
                    onClick={() => setNewChallengeOpen(true)}
                  >
                    <Icon icon={PlusIcon} />
                    {!theme.sizes.isComputer
                      ? `Create ${t("generic.challengeWithArticle")}`
                      : t("challenges.new.title")}
                  </Button>
                )}
              </GridColumn>
            </GridRow>
          </Grid>
        </Container>
      </div>

      <Container style={{ marginTop: "1rem" }}>
        <Grid stackable>
          <GridRow>
            <GridColumn>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 24,
                }}
              >
                <Input
                  placeholder={t("challenges.all.search")}
                  loading={isLoading}

                  onChange={(_e, { value }) => getChallenges(value)}
                  value={searchQuery}
                  style={{ width: "33%" }}
                />
                {!challenges?.length && !isLoading ? (
                  searchQuery ? (
                    <EmptyBox style={{ width: "100%" }}>
                      <img
                        src={DiscoverImage}
                        style={{
                          maxHeight: 150,
                          maxWidth: "70%",
                          display: "block",
                          margin: "10px auto",
                        }}
                        alt="Discover"
                      />
                      <H2>{t("ideas.yours.sidebar.noResults")}</H2>
                    </EmptyBox>
                  ) : (
                    <>
                      <EmptyBox style={{ width: "100%" }}>
                        <img
                          src={DiscoverImage}
                          style={{
                            maxHeight: 150,
                            maxWidth: "70%",
                            display: "block",
                            margin: "10px auto",
                          }}
                          alt="Discover"
                        />

                        <H2>{t("challenges.all.noneOpen")}</H2>
                      </EmptyBox>
                      <Divider hidden />
                    </>
                  )
                ) : (
                  <>
                    <div style={{ width: "100%" }}>
                      <CardGroup itemsPerRow={4} stackable>
                        {!challenges?.length && isLoading
                          ? Array.from({ length: 8 }).map((_, i) => (
                              <Card key={i}>
                                <Placeholder>
                                  <PlaceholderParagraph>
                                    <PlaceholderLine style={{ height: 200 }} />
                                    <PlaceholderLine style={{ height: 50 }} />
                                  </PlaceholderParagraph>
                                </Placeholder>
                              </Card>
                            ))
                          : challenges?.map((challenge) => (
                              <ChallengeCard
                                showIdeas
                                challenge={challenge}
                                ideas={challenge.ideas}
                                key={challenge._id}
                              />
                            ))}
                      </CardGroup>
                    </div>
                    <Divider />
                  </>
                )}
              </div>
            </GridColumn>
          </GridRow>
        </Grid>
        <Divider hidden />
      </Container>
    </div>
  );
};

export default Challenges;
